import { ErrorWrapper } from '@rsos/base-components/form/ErrorsWrapper';
import { Select } from '@rsos/components/capstone/base';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import { useSelectStyles } from '../TowPro/shared';
import { callerIdMatchesInput } from '../TowPro/towProCallLookup';
import FieldLabel from './FieldLabel';
import styles from './NorfolkSouthernForm.module.css';
import {
  visibleFieldError,
  type NorfolkSouthernFormik,
  type SelectOption,
} from './formFieldUtils';

/**
 * One option per call in the queue, which is keyed by caller ID. A typed
 * number, or a call that has since left the queue, stays listed so the field
 * still shows it.
 */
function buildRelatedCallOptions(
  callerIds: string[],
  currentCallerId: string,
): SelectOption[] {
  const options =
    currentCallerId && !callerIds.includes(currentCallerId)
      ? [currentCallerId, ...callerIds]
      : callerIds;
  return options.map(callerId => ({
    value: callerId,
    label: formatInternationalPhoneNumberHuman(callerId),
  }));
}

const filterRelatedCallOption = (
  option: { data: SelectOption },
  input: string,
): boolean => callerIdMatchesInput(option.data.value, input);

interface RelatedCallFieldProps {
  formik: NorfolkSouthernFormik;
  /** Caller IDs of the calls in the queue. */
  callerIds: string[];
}

/** Picks the related call from the call queue, or takes a typed number. */
function RelatedCallField({
  formik,
  callerIds,
}: RelatedCallFieldProps): JSX.Element {
  const options = buildRelatedCallOptions(callerIds, formik.values.relatedCall);
  const error = visibleFieldError(formik, 'relatedCall');
  const selectStyles = useSelectStyles(error);

  return (
    <div className={styles.field}>
      <FieldLabel>Related Call</FieldLabel>
      <Select
        name="relatedCall"
        classNamePrefix="norfolk-southern-related-call"
        options={options}
        placeholder="Search calls or enter a number"
        isClearable
        isSearchable
        filterOption={filterRelatedCallOption}
        creatable
        isValidNewOption={(input: string) =>
          input.trim().length > 0 &&
          !options.some(option => callerIdMatchesInput(option.value, input))
        }
        // Only the value is stored, so the field shows the plain typed
        // number once picked rather than the "Use" prompt.
        getNewOptionData={(input: string, label: string): SelectOption => ({
          value: input.trim(),
          label,
        })}
        formatCreateLabel={(input: string) => `Use "${input.trim()}"`}
        onChange={(option: SelectOption | null) => {
          formik.setFieldValue('relatedCall', option?.value ?? '', true);
          formik.setFieldTouched('relatedCall', true, false);
        }}
        onBlur={() => formik.setFieldTouched('relatedCall', true, false)}
        value={
          options.find(option => option.value === formik.values.relatedCall) ??
          null
        }
        styles={selectStyles}
      />
      {error && <ErrorWrapper error={error} />}
    </div>
  );
}

export default RelatedCallField;
